import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useMemo } from "react";
import classnames from "classnames";
import { GenericConfigContext } from "/contexts/generic-config-context";
import { getCurrencyName, getCurrencyWithPriceDisplay, getCurrencySign, shouldShowCurrencyText, CURRENCY_SIGN_RIGHT_OF_NUMBER_LOCALES } from "/services/pricing-service";
import { getFreeTierSeatsCountByAbTest } from "services/ab-tests/monetization-ab-tests-service";
import { TIERS } from "constants/pricing";
import { calculateFixedPricing, getPricingDisplayConfig } from "services/pricing/fixed-pricing-service";
import { handleRuntimeError } from "client/services/exceptions-handling-service/exceptions-handling-service";
import MondayMarkupLanguageComponent from "client/components/system/monday-markup-language/monday-markup-language-component";
import { StyledProductPrice } from "./product-price.scss";
import { PRICING_TYPES } from "segments/desktop/pricing/constants";
import { VERSION_16, VERSION_17, DEFAULT_PRODUCT_PRICING_VERSION } from "constants/pricing-versions";
import { renderTotalPriceWithAddon } from "../../../../addons/addon-price-renderer";
import { CREDITS_PER_TIER, TIERS_WITH_SPECIAL_OFFER, getCreditPrice, formatPriceWithCurrency } from "constants/pricing/credits-config";
import { CREDITS_PER_SEAT_YEARLY_SAVINGS_TOOLTIP_KEY } from "../tier-ai-block/tier-ai-block-constants";
export const ProductPrice = ({ period, seats, tierName, product, hideFreeForever, splitCurrencySign, isNewPricing, unitOfMeasure, crossedPricingUI, crmClassic, hideDiscounts, showCrossedOutPrice, addon, addonTotalPrice, selectedCredits })=>{
    var _pricingResult_totalPricePerMonth, _pricingResult_totalPrice;
    const { translate, translateOrTranslateWithParam, currency: currencyFromContext, pricingVersion, localeId, forceCurrency, cloudfrontViewerCountry, cookies, forcePricing, abTests, useFreeForeverPriceCardKey } = useContext(GenericConfigContext);
    const currency = currencyFromContext || getCurrencyName({
        forceCurrency,
        countryCode: cloudfrontViewerCountry
    });
    const productPricingVersion = forcePricing ? pricingVersion : (DEFAULT_PRODUCT_PRICING_VERSION === null || DEFAULT_PRODUCT_PRICING_VERSION === void 0 ? void 0 : DEFAULT_PRODUCT_PRICING_VERSION[product]) || pricingVersion;
    // Credits-per-seat variant only — control keeps current (v15) prices; v17 is the price-increase arm.
    const effectivePricingVersion = showCrossedOutPrice ? VERSION_17 : productPricingVersion;
    const pricingResult = useMemo(()=>calculateFixedPricing({
            product,
            pricingVersion: effectivePricingVersion,
            period,
            tierName,
            currency,
            unitOfMeasure,
            quantity: seats
        }), [
        product,
        effectivePricingVersion,
        period,
        tierName,
        currency,
        unitOfMeasure,
        seats
    ]);
    const displayConfig = useMemo(()=>getPricingDisplayConfig(pricingResult, unitOfMeasure), [
        pricingResult,
        unitOfMeasure
    ]);
    if (!pricingResult.isValid) {
        const params = {
            product,
            period,
            currency,
            tierName,
            seats,
            unitOfMeasure
        };
        handleRuntimeError(`ProductPrice: error calculating pricing. Params: ${JSON.stringify(params)}`);
    }
    const pricePerSeat = getCurrencyWithPriceDisplay({
        price: (_pricingResult_totalPricePerMonth = pricingResult.totalPricePerMonth) === null || _pricingResult_totalPricePerMonth === void 0 ? void 0 : _pricingResult_totalPricePerMonth.toLocaleString(),
        currency,
        localeId,
        hideCurrency: splitCurrencySign
    });
    const totalPrice = getCurrencyWithPriceDisplay({
        price: pricingResult === null || pricingResult === void 0 ? void 0 : (_pricingResult_totalPrice = pricingResult.totalPrice) === null || _pricingResult_totalPrice === void 0 ? void 0 : _pricingResult_totalPrice.toLocaleString(),
        currency,
        localeId,
        className: "product-price-total",
        unitOfMeasure
    });
    const currencySignDisplay = useMemo(()=>getCurrencySign(currency), [
        currency
    ]);
    const renderUpToSeats = ()=>{
        return /*#__PURE__*/ _jsx("div", {
            className: "product-price-total",
            children: translateOrTranslateWithParam({
                key: "pricingPage.upToXSeats",
                param: getFreeTierSeatsCountByAbTest({
                    cookies,
                    abTests,
                    forcePricing
                })
            })
        });
    };
    const baseCredits = CREDITS_PER_TIER[tierName];
    const isCreditsPricing = selectedCredits !== undefined && baseCredits !== undefined;
    const renderCreditsPricePerMonth = ()=>{
        var _pricingResult_totalPrice;
        const seatsPrice = (_pricingResult_totalPrice = pricingResult === null || pricingResult === void 0 ? void 0 : pricingResult.totalPrice) !== null && _pricingResult_totalPrice !== void 0 ? _pricingResult_totalPrice : 0;
        var _pricingResult_totalPricePerMonth;
        const seatPrice = (_pricingResult_totalPricePerMonth = pricingResult === null || pricingResult === void 0 ? void 0 : pricingResult.totalPricePerMonth) !== null && _pricingResult_totalPricePerMonth !== void 0 ? _pricingResult_totalPricePerMonth : 0;
        const selectedCreditsPrice = getCreditPrice({
            credits: selectedCredits,
            period,
            currency
        });
        const defaultCreditsValue = getCreditPrice({
            credits: baseCredits,
            period,
            currency
        });
        const applyDiscount = !hideDiscounts && seats < 20;
        const hideDiscountUi = hideDiscounts || !applyDiscount || !TIERS_WITH_SPECIAL_OFFER.includes(tierName) || period === "monthly";
        const total = applyDiscount ? seatsPrice + selectedCreditsPrice - defaultCreditsValue : seatsPrice + selectedCreditsPrice;
        const originalTotal = seatsPrice + selectedCreditsPrice;
        const formatPrice = (price)=>formatPriceWithCurrency({
                amount: price !== null && price !== void 0 ? price : 0,
                currency,
                localeId
            });
        const billedTextRaw = translate(`pricingPage.periods.${period}.billed`) || "";
        const billedText = billedTextRaw.charAt(0).toLowerCase() + billedTextRaw.slice(1);
        const seatsLine = (translate("pricingPage.credits.tooltip.seatsLine") || "").replace("{seatPrice}", formatPrice(seatPrice)).replace("{seats}", seats).replace("{seatsTotal}", formatPrice(seatsPrice));
        const creditsLine = (translate("pricingPage.credits.tooltip.creditsLine") || "").replace("{credits}", selectedCredits.toLocaleString(localeId || undefined)).replace("{creditsPrice}", formatPrice(selectedCreditsPrice));
        const discountLine = (translate("pricingPage.credits.tooltip.discountLine") || "").replace("{discount}", formatPrice(defaultCreditsValue));
        const totalLine = (translate("pricingPage.credits.tooltip.totalLine") || "").replace("{total}", formatPrice(total));
        const renderTooltipLine = (line)=>{
            const eqIdx = line.lastIndexOf(" = ");
            if (eqIdx === -1) return /*#__PURE__*/ _jsx("div", {
                className: "credits-tooltip-line",
                children: line
            });
            return /*#__PURE__*/ _jsxs("div", {
                className: "credits-tooltip-line",
                children: [
                    line.slice(0, eqIdx + 3),
                    /*#__PURE__*/ _jsx("span", {
                        className: "credits-tooltip-value",
                        children: line.slice(eqIdx + 3)
                    })
                ]
            });
        };
        return /*#__PURE__*/ _jsx("div", {
            className: "product-price-total-wrapper credits-price-wrapper",
            children: /*#__PURE__*/ _jsxs("span", {
                className: "product-price-total",
                children: [
                    translate("pricingPage.totalOf"),
                    " ",
                    /*#__PURE__*/ _jsxs("span", {
                        className: "credits-price-with-tooltip",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "product-price-total credits-nowrap",
                                children: formatPrice(total)
                            }),
                            !hideDiscountUi && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    " ",
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "product-price-total credits-nowrap credits-price-original",
                                        children: formatPrice(originalTotal)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "credits-tooltip",
                                children: [
                                    renderTooltipLine(seatsLine),
                                    renderTooltipLine(creditsLine),
                                    applyDiscount && renderTooltipLine(discountLine),
                                    renderTooltipLine(totalLine)
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "product-price-total credits-billed-text",
                        children: [
                            " ",
                            translate("pricingPage.credits.perMonthSuffix"),
                            ", ",
                            billedText
                        ]
                    })
                ]
            })
        });
    };
    const renderPricePerMonth = ()=>{
        if (isCreditsPricing) {
            return renderCreditsPricePerMonth();
        }
        const totalPriceWithAddon = renderTotalPriceWithAddon({
            basePriceFormatted: totalPrice,
            basePriceRaw: pricingResult === null || pricingResult === void 0 ? void 0 : pricingResult.totalPrice,
            addon,
            addonTotalPrice,
            currency,
            localeId,
            translateOrTranslateWithParam,
            includeTotalPrefix: displayConfig.showTotalLabel
        });
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "product-price-total-wrapper",
                    children: [
                        displayConfig.showTotalLabel && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "product-price-total",
                                    children: totalPriceWithAddon
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "product-price-total",
                                    children: translate("pricingPage.perMonthSlash")
                                })
                            ]
                        }),
                        !displayConfig.showTotalLabel && /*#__PURE__*/ _jsx(_Fragment, {
                            children: displayConfig.pricingType === PRICING_TYPES.FIXED ? /*#__PURE__*/ _jsxs("div", {
                                className: "price-with-unit",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `product-price-total contacts-pricing-unit`,
                                        children: totalPriceWithAddon
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `product-price-total contacts-text-unit`,
                                        children: translate("pricingPage.perMonthSlash")
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "product-price-total",
                                        children: totalPriceWithAddon
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "product-price-total",
                                        children: translate("pricingPage.perMonthSlash")
                                    })
                                ]
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "product-price-billed-periodically",
                    children: translate(`pricingPage.periods.${period}.billed`)
                })
            ]
        });
    };
    const renderFreeForever = ()=>{
        if (useFreeForeverPriceCardKey) {
            return /*#__PURE__*/ _jsx(MondayMarkupLanguageComponent, {
                tagName: "span",
                text: translate("pricingPage.freeForeverPriceCard")
            });
        }
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                translate("pricingPage.free"),
                /*#__PURE__*/ _jsx("br", {}),
                " ",
                translate("pricingPage.forever")
            ]
        });
    };
    const renderSeatPerMonth = ({ inline = false } = {})=>{
        if (inline) {
            return /*#__PURE__*/ _jsxs("span", {
                className: "seat-month-inline",
                children: [
                    translate("pricingPage.seatOver"),
                    " ",
                    translate("pricingPage.month")
                ]
            });
        }
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                translate("pricingPage.seatOver"),
                /*#__PURE__*/ _jsx("br", {}),
                " ",
                translate("pricingPage.month")
            ]
        });
    };
    const renderDiscountChip = ()=>{
        const isCurrencySupportPriceIncrease = [
            "usd",
            "eur",
            "euro"
        ].includes(currency);
        return /*#__PURE__*/ _jsx("div", {
            className: "discount-chip",
            children: translateOrTranslateWithParam({
                key: "pricingPage.percentageOff",
                param: crossedPricingUI && isCurrencySupportPriceIncrease ? 33 : 18
            })
        });
    };
    const renderCrossedOutPrice = ()=>{
        // Credits-per-seat / price-increase: struck = monthly list, left = yearly (18% off). Free has no list price.
        if (period === "monthly" || tierName === TIERS.FREE) {
            return null;
        }
        const monthlyPricingResult = calculateFixedPricing({
            product,
            pricingVersion: getCrossedOutPricingVersion(),
            period: "monthly",
            tierName,
            currency,
            unitOfMeasure,
            quantity: seats
        });
        return /*#__PURE__*/ _jsxs("div", {
            className: `crossed-out-price${crmClassic ? " before-amount" : ""}`,
            children: [
                currencySignDisplay,
                monthlyPricingResult.totalPricePerMonth
            ]
        });
    };
    const getCrossedOutPricingVersion = ()=>{
        if (showCrossedOutPrice) return VERSION_17;
        if (crossedPricingUI) return VERSION_16;
        return pricingVersion;
    };
    const getYearlySavingsPerSeatAmount = ()=>{
        if (!showCrossedOutPrice || period === "monthly" || tierName === TIERS.FREE) return null;
        const monthlyPricingResult = calculateFixedPricing({
            product,
            pricingVersion: VERSION_17,
            period: "monthly",
            tierName,
            currency,
            unitOfMeasure,
            quantity: 1
        });
        const yearlyPricingResult = calculateFixedPricing({
            product,
            pricingVersion: VERSION_17,
            period: "yearly",
            tierName,
            currency,
            unitOfMeasure,
            quantity: 1
        });
        const yearlyMonthlyRate = yearlyPricingResult === null || yearlyPricingResult === void 0 ? void 0 : yearlyPricingResult.totalPricePerMonth;
        const monthlyRate = monthlyPricingResult === null || monthlyPricingResult === void 0 ? void 0 : monthlyPricingResult.totalPricePerMonth;
        if (yearlyMonthlyRate == null || monthlyRate == null) return null;
        const savings = (monthlyRate - yearlyMonthlyRate) * 12;
        return savings > 0 ? savings : null;
    };
    const renderYearlySavingsTooltip = ()=>{
        const savings = getYearlySavingsPerSeatAmount();
        if (savings == null) return null;
        const amount = formatPriceWithCurrency({
            amount: savings,
            currency,
            localeId
        });
        const tooltipText = translateOrTranslateWithParam({
            key: CREDITS_PER_SEAT_YEARLY_SAVINGS_TOOLTIP_KEY,
            param: amount
        });
        return /*#__PURE__*/ _jsx("div", {
            id: yearlySavingsTooltipId,
            role: "tooltip",
            className: "yearly-savings-tooltip",
            children: tooltipText
        });
    };
    const renderNewPriceDescription = ()=>{
        return /*#__PURE__*/ _jsxs("div", {
            className: "price-description-new",
            children: [
                translate("pricingPage.perSeatPerMonth"),
                ", ",
                period === "monthly" ? translate("pricingPage.billedMonthly") : translate("pricingPage.billedAnnually")
            ]
        });
    };
    const shouldSeeFreeForever = useMemo(()=>{
        return tierName === TIERS.FREE && !hideFreeForever;
    }, [
        hideFreeForever,
        tierName
    ]);
    const showYearlySavingsTooltip = getYearlySavingsPerSeatAmount() != null;
    const yearlySavingsTooltipId = `yearly-savings-tooltip-${tierName}`;
    const showCreditsPerSeatCrossedMeta = showCrossedOutPrice && period !== "monthly" && tierName !== TIERS.FREE;
    const seatLabelInline = !!showCrossedOutPrice;
    const renderPriceDescription = ()=>{
        if (!displayConfig.showPriceDescription) return null;
        return /*#__PURE__*/ _jsxs("div", {
            className: "price-description",
            children: [
                shouldSeeFreeForever && renderFreeForever(),
                !shouldSeeFreeForever && !isNewPricing && renderSeatPerMonth({
                    inline: seatLabelInline
                }),
                !hideDiscounts && !crossedPricingUI && isNewPricing && period === "yearly" && renderDiscountChip()
            ]
        });
    };
    return /*#__PURE__*/ _jsxs(StyledProductPrice, {
        className: "price",
        isNewPricing: isNewPricing,
        isCreditsPricing: isCreditsPricing,
        crmClassic: crmClassic,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: classnames("price-tag", {
                    "has-yearly-savings-tooltip": showYearlySavingsTooltip
                }),
                tabIndex: showYearlySavingsTooltip ? 0 : undefined,
                "aria-describedby": showYearlySavingsTooltip ? yearlySavingsTooltipId : undefined,
                children: [
                    crmClassic && crossedPricingUI && isNewPricing && renderCrossedOutPrice(),
                    splitCurrencySign && displayConfig.showCurrencySign && !CURRENCY_SIGN_RIGHT_OF_NUMBER_LOCALES.includes(localeId) && /*#__PURE__*/ _jsxs("div", {
                        className: `currency-sign`,
                        children: [
                            currencySignDisplay,
                            shouldShowCurrencyText(currency) && /*#__PURE__*/ _jsx("div", {
                                className: "price-currency-text",
                                children: currency.toUpperCase()
                            })
                        ]
                    }),
                    displayConfig.showAmount && /*#__PURE__*/ _jsx("div", {
                        className: "amount",
                        children: pricePerSeat
                    }),
                    splitCurrencySign && displayConfig.showCurrencySign && CURRENCY_SIGN_RIGHT_OF_NUMBER_LOCALES.includes(localeId) && /*#__PURE__*/ _jsxs("div", {
                        className: `currency-sign`,
                        children: [
                            currencySignDisplay,
                            shouldShowCurrencyText(currency) && /*#__PURE__*/ _jsx("div", {
                                className: "price-currency-text",
                                children: currency.toUpperCase()
                            })
                        ]
                    }),
                    showCreditsPerSeatCrossedMeta ? /*#__PURE__*/ _jsxs("div", {
                        className: "crossed-price-meta",
                        children: [
                            renderCrossedOutPrice(),
                            renderPriceDescription()
                        ]
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            !crmClassic && crossedPricingUI && isNewPricing && renderCrossedOutPrice(),
                            crmClassic && /*#__PURE__*/ _jsxs("div", {
                                className: "price-description-inline",
                                children: [
                                    translate("pricingPage.seatOver"),
                                    /*#__PURE__*/ _jsx("br", {}),
                                    translate("pricingPage.month")
                                ]
                            }),
                            renderPriceDescription()
                        ]
                    }),
                    renderYearlySavingsTooltip()
                ]
            }),
            tierName === TIERS.FREE && !isNewPricing && renderUpToSeats(),
            tierName !== TIERS.FREE && !isNewPricing && renderPricePerMonth(),
            isNewPricing && !crmClassic && renderNewPriceDescription(),
            /*#__PURE__*/ _jsx("div", {})
        ]
    });
};
